/**
 * Per-entity-type paginated list hook for the Research drill-in page.
 *
 * Wraps `useInfiniteQuery` so the "Load more" / infinite-scroll affordance
 * gets stable cursor-based pagination, dedupe on type changes, and the
 * React-Query cache layer for free.
 */
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  type EntityListOrder,
  type EntityListPage,
  type EntityListSort,
  listCaseEntitiesByType,
} from '@/services/api/case-search'

export interface UseEntityTypeListParams {
  caseId: string
  typeLabel: string
  query?: string
  sort?: EntityListSort
  order?: EntityListOrder
  limit?: number
  /** Only the entities this document yielded. */
  documentId?: string
}

export function useEntityTypeList({
  caseId,
  typeLabel,
  query = '',
  sort = 'mentions',
  order = 'desc',
  limit = 50,
  documentId,
}: UseEntityTypeListParams) {
  const trimmed = query.trim()
  return useInfiniteQuery<EntityListPage>({
    queryKey: ['case-entities-by-type', caseId, typeLabel, trimmed, sort, order, limit, documentId],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      listCaseEntitiesByType(caseId, typeLabel, {
        query: trimmed || undefined,
        sort,
        order,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
        limit,
        documentId,
      }),
    getNextPageParam: last => last.next_cursor ?? undefined,
    // The filter input is already debounced at the page level — no need
    // for React-Query staleness shenanigans.
    staleTime: 0,
  })
}
